/**
 * Utilitário para avaliar se um campo personalizado condicional deve ser exibido
 * (e portanto se sua obrigatoriedade deve ser exigida).
 *
 * Operador `between`: conditional_value = JSON {"min":"...","max":"..."} (inclusivo).
 */

import { normalizeChangeTypeCode } from '../pages/changes/changeTypeConditionalMatch';

export type ConditionalBetweenBounds = { min: string; max: string };

/** Shape mínimo usado na avaliação de visibilidade condicional. */
export type ConditionalFieldLike = {
  id?: number;
  native_key?: string | null;
  conditional_field_id?: number | null;
  conditional_value?: string | null;
  conditional_operator?: string | null;
  field_type?: string;
  allow_multiple?: boolean;
};

/** Parseia conditional_value no formato between. Retorna null se inválido. */
export function parseBetweenBounds(raw: unknown): ConditionalBetweenBounds | null {
  if (raw === undefined || raw === null || raw === '') return null;

  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const obj = parsed as Record<string, unknown>;
  const min = obj.min !== undefined && obj.min !== null ? String(obj.min).trim() : '';
  const max = obj.max !== undefined && obj.max !== null ? String(obj.max).trim() : '';
  if (!min || !max) return null;
  return { min, max };
}

export function serializeBetweenBounds(min: string, max: string): string {
  return JSON.stringify({ min: String(min).trim(), max: String(max).trim() });
}

export function parseNumericValue(rawValue: unknown, fieldType?: string): number {
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return NaN;
  }
  if (fieldType === 'currency') {
    try {
      const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
      if (parsed && typeof parsed === 'object' && parsed !== null && 'value' in parsed) {
        const num = parseFloat(String((parsed as { value?: unknown }).value).replace(',', '.'));
        return Number.isNaN(num) ? NaN : num;
      }
    } catch {
      // fallback abaixo
    }
  }
  const rawStr = String(rawValue).trim().replace(/\s/g, '');
  // Aceita "1.234,56" (BRL) ou "1234.56"
  if (/^\d{1,3}(\.\d{3})*,\d+$/.test(rawStr)) {
    const normalized = rawStr.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(normalized);
    return Number.isNaN(num) ? NaN : num;
  }
  const parsed = parseFloat(rawStr.replace(/[^\d.-]/g, ''));
  return Number.isNaN(parsed) ? NaN : parsed;
}

function compareNumeric(fieldNum: number, conditionNum: number, op: string): boolean {
  if (Number.isNaN(fieldNum) || Number.isNaN(conditionNum)) return false;
  switch (op) {
    case 'equals':
      return Math.abs(fieldNum - conditionNum) < 0.01;
    case 'not_equals':
      return Math.abs(fieldNum - conditionNum) >= 0.01;
    case 'greater_than':
      return fieldNum > conditionNum;
    case 'greater_equal':
      return fieldNum >= conditionNum;
    case 'less_than':
      return fieldNum < conditionNum;
    case 'less_equal':
      return fieldNum <= conditionNum;
    default:
      return false;
  }
}

function parseDateOnly(raw: unknown): Date | null {
  const s = String(raw || '').trim();
  if (!s) return null;
  const iso = s.length >= 10 ? s.slice(0, 10) : s;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function compareDates(fieldDate: Date, conditionDate: Date, op: string): boolean {
  switch (op) {
    case 'equals':
      return fieldDate.toDateString() === conditionDate.toDateString();
    case 'not_equals':
      return fieldDate.toDateString() !== conditionDate.toDateString();
    case 'greater_than':
      return fieldDate > conditionDate;
    case 'greater_equal':
      return fieldDate >= conditionDate;
    case 'less_than':
      return fieldDate < conditionDate;
    case 'less_equal':
      return fieldDate <= conditionDate;
    default:
      return false;
  }
}

function normalizeScalarComparisonValue(rawValue: unknown): string {
  if (rawValue === undefined || rawValue === null) return '';
  if (Array.isArray(rawValue)) {
    const first = rawValue.find((item) => String(item ?? '').trim() !== '');
    return first === undefined ? '' : String(first).toLowerCase().trim();
  }
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) return '';
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const first = parsed.find((item) => String(item ?? '').trim() !== '');
        return first === undefined ? '' : String(first).toLowerCase().trim();
      }
      if (typeof parsed === 'boolean') {
        return parsed ? 'true' : 'false';
      }
      if (parsed === null || parsed === undefined) return '';
      return String(parsed).toLowerCase().trim();
    } catch {
      return trimmed.toLowerCase().trim();
    }
  }
  if (typeof rawValue === 'boolean') {
    return rawValue ? 'true' : 'false';
  }
  return String(rawValue).toLowerCase().trim();
}

function evaluateBetween(
  fieldVal: unknown,
  bounds: ConditionalBetweenBounds,
  fieldType?: string
): boolean {
  if (['integer', 'decimal', 'currency', 'score'].includes(fieldType || '')) {
    const fieldNum = parseNumericValue(fieldVal, fieldType);
    const minNum = parseNumericValue(bounds.min, fieldType === 'currency' ? undefined : fieldType);
    const maxNum = parseNumericValue(bounds.max, fieldType === 'currency' ? undefined : fieldType);
    if (Number.isNaN(fieldNum) || Number.isNaN(minNum) || Number.isNaN(maxNum)) return false;
    if (minNum > maxNum) return false;
    return fieldNum >= minNum && fieldNum <= maxNum;
  }

  if (fieldType === 'date') {
    const fieldDate = parseDateOnly(fieldVal);
    const minDate = parseDateOnly(bounds.min);
    const maxDate = parseDateOnly(bounds.max);
    if (!fieldDate || !minDate || !maxDate) return false;
    if (minDate > maxDate) return false;
    return fieldDate >= minDate && fieldDate <= maxDate;
  }

  return false;
}

/**
 * Retorna true se o campo deve ser exibido dadas as condições e os valores atuais.
 * Usado na validação de obrigatoriedade: só exige preenchimento quando o campo seria exibido.
 */
export function shouldShowConditionalField(
  field: ConditionalFieldLike,
  customFields: ConditionalFieldLike[],
  values: Record<number, unknown>
): boolean {
  const hasCondValue =
    field.conditional_value !== undefined &&
    field.conditional_value !== null &&
    String(field.conditional_value).trim() !== '';
  if (hasCondValue && !field.conditional_field_id) {
    return false;
  }
  if (!field.conditional_field_id || !field.conditional_value) {
    return true; // Sem condição, sempre exibir
  }

  let conditionalFieldValue = values[field.conditional_field_id];
  if (
    conditionalFieldValue === undefined ||
    conditionalFieldValue === null ||
    conditionalFieldValue === ''
  ) {
    return false; // Campo condicional vazio, não exibir
  }

  const conditionalField = customFields.find((f) => f.id === field.conditional_field_id);
  const fieldType = conditionalField?.field_type;
  const operator = field.conditional_operator || 'equals';

  if (operator === 'between') {
    const bounds = parseBetweenBounds(field.conditional_value);
    if (!bounds) return false;
    return evaluateBetween(conditionalFieldValue, bounds, fieldType);
  }

  // Converter para número se o campo condicional for numérico (compatível com Purchases)
  if (conditionalField && ['integer', 'decimal', 'currency', 'score'].includes(conditionalField.field_type || '')) {
    if (typeof conditionalFieldValue === 'string') {
      try {
        const parsed = JSON.parse(conditionalFieldValue);
        if (typeof parsed === 'object' && parsed !== null) {
          if ('value' in parsed && parsed.value !== undefined) {
            conditionalFieldValue = parseFloat(String(parsed.value)) || 0;
          } else {
            conditionalFieldValue =
              typeof parsed === 'number' ? parsed : parseFloat(conditionalFieldValue) || 0;
          }
        } else if (typeof parsed === 'number') {
          conditionalFieldValue = parsed;
        } else {
          conditionalFieldValue = parseFloat(conditionalFieldValue) || 0;
        }
      } catch {
        conditionalFieldValue = parseFloat(String(conditionalFieldValue)) || 0;
      }
    }
  }

  let conditionalValues: string[] = [];
  try {
    const parsed = JSON.parse(field.conditional_value);
    if (Array.isArray(parsed)) {
      conditionalValues = parsed.map((v) => String(v).trim());
    } else if (parsed !== null && typeof parsed === 'object') {
      // Objeto sem ser between (já tratado): não usar como OR
      conditionalValues = [String(field.conditional_value).trim()];
    } else {
      conditionalValues = [String(parsed).trim()];
    }
  } catch {
    conditionalValues = [String(field.conditional_value).trim()];
  }

  function compareValues(fieldVal: unknown, conditionVal: string, op: string): boolean {
    if (['integer', 'decimal', 'currency', 'score'].includes(fieldType || '')) {
      const fieldNum = parseNumericValue(fieldVal, fieldType);
      const conditionNum = parseNumericValue(conditionVal, fieldType === 'currency' ? undefined : fieldType);
      return compareNumeric(fieldNum, conditionNum, op);
    }

    if (fieldType === 'date') {
      const fieldDate = parseDateOnly(fieldVal);
      const conditionDate = parseDateOnly(conditionVal);
      if (!fieldDate || !conditionDate) return false;
      return compareDates(fieldDate, conditionDate, op);
    }

    if (fieldType === 'check' && conditionalField?.allow_multiple) {
      let selectedValues: string[] = [];
      if (Array.isArray(fieldVal)) {
        selectedValues = fieldVal.map((v) => String(v).toLowerCase().trim());
      } else if (typeof fieldVal === 'string') {
        try {
          const parsed = JSON.parse(fieldVal);
          if (Array.isArray(parsed)) {
            selectedValues = parsed.map((v) => String(v).toLowerCase().trim());
          } else {
            selectedValues = [String(parsed).toLowerCase().trim()];
          }
        } catch {
          selectedValues = String(fieldVal)
            .split(',')
            .map((v) => v.toLowerCase().trim())
            .filter((v) => v.length > 0);
        }
      } else if (fieldVal !== null && fieldVal !== undefined) {
        selectedValues = [String(fieldVal).toLowerCase().trim()];
      }
      const normalizedCondition = conditionVal.toLowerCase().trim();
      switch (op) {
        case 'not_equals':
          return !selectedValues.includes(normalizedCondition);
        case 'contains':
        case 'equals':
        default:
          return selectedValues.includes(normalizedCondition);
      }
    }

    let fieldStr = normalizeScalarComparisonValue(fieldVal);
    let conditionStr = conditionVal.toLowerCase().trim();
    if (conditionalField?.native_key === 'change_type') {
      fieldStr = normalizeChangeTypeCode(String(fieldVal ?? ''));
      conditionStr = normalizeChangeTypeCode(conditionVal);
    }
    switch (op) {
      case 'equals':
        return fieldStr === conditionStr;
      case 'not_equals':
        return fieldStr !== conditionStr;
      case 'contains':
        return fieldStr.includes(conditionStr);
      default:
        return fieldStr === conditionStr;
    }
  }

  if (conditionalValues.length > 1) {
    return conditionalValues.some((val) => compareValues(conditionalFieldValue, val, operator));
  }
  return compareValues(conditionalFieldValue, conditionalValues[0], operator);
}
